import { type FC, useMemo, useState } from "react"
import styled, { css } from "styled-components"
import tinycolor from "tinycolor2"
import Values from "values.js"

import { parseIntNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useVideos } from "~/api/video"
import { usePlatform } from "~/contexts/PlatformContext"
import useQueryParam from "~/hooks/useQueryParam"
import Button from "~/themes/school/components/Button"
import Card, { CardText, CardThumbnail, CardThumbnailSticker, CardTitle, Cards } from "~/themes/school/components/Card"
import ContentSearch from "~/themes/school/components/ContentSearch"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const VideosPage: FC = () => {
	const videos = useVideos()
	const platform = usePlatform().platform
	const t = useTranslation()
	const selectedTag = useQueryParam("tag")

	const [searchItems, setSearchItems] = useState<number[]>()

	const keys = useMemo(() => ["name" as const], [])

	const selectedTagId = parseIntNumber(selectedTag.value)

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("video.title")}</PageTitle>
				<ContentSearch
					keys={keys}
					items={videos.status === "success" ? videos.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{videos.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : videos.status === "loading" ? (
				<PartialLoadingPage />
			) : videos.status === "success" ? (
				<>
					{videos.tags.length > 0 && (
						<Tags>
							{videos.tags.map(tag => (
								<Tag
									key={tag.id}
									$isSelected={selectedTagId === tag.id}
									onClick={() => selectedTag.set(tag.id.toString())}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag.value !== null && (
								<Tag $isSelected onClick={() => selectedTag.set(null)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Cards>
						{(selectedTagId !== null
							? videos.data.filter(video => video.tagIds.includes(selectedTagId))
							: videos.data
						)
							.filter(x => searchItems?.includes(x.id) ?? true)
							.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
							.map(video => (
								<Card key={video.id} as={Button} onClick={routes.video.detail(video.id)}>
									{platform.id !== CONFIG.bokaderoPlatformId && <CardTitle>{video.name}</CardTitle>}
									<CardThumbnail
										filePath={video.thumbnailUrl}
										sticker={<CardThumbnailSticker>{video.priceLabel.value}</CardThumbnailSticker>}
									/>
									{platform.id === CONFIG.bokaderoPlatformId && <CardTitle>{video.name}</CardTitle>}
									{video.shortDescription && <CardText>{video.shortDescription}</CardText>}
								</Card>
							))}
					</Cards>
				</>
			) : (
				exhaustiveGuard(videos.status)
			)}
		</Layout>
	)
}

const Tags = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 16px;

	@media (max-width: 999px) {
		gap: 8px;
		margin-left: 16px;
		margin-right: 16px;
	}
`

const Tag = styled(Button)<{ $isSelected: boolean }>(props => {
	const color = props.$isSelected
		? props.theme.primaryColor
		: new Values(props.theme.primaryColor).tint(50).hexString()

	return css`
		padding: 12px 24px;
		border-radius: 40px;
		background-color: ${color};
		color: ${tinycolor(color).isDark() ? "white" : "black"};
		line-height: 1;
	`
})

export default VideosPage
